












































import type { PropType, Ref } from '@vue/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import { IconSize, ButtonType } from '@getaccept/lib-ui';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  InputDocNode,
  ParagraphNode,
  MergeValueNode,
  EditorHelper,
} from '@getaccept/editor-lib';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import Text from '@tiptap/extension-text';
import Focus from '@tiptap/extension-focus';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type Vue from 'vue';
import Placeholder from '@tiptap/extension-placeholder';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';

export default defineComponent({
  components: {
    EditorContent,
  },
  props: {
    hasUpdateError: { type: Boolean },
    fieldFocused: { type: Boolean },
    inputField: { type: Object as PropType<Field>, required: true },
    recipientInput: { type: Object as PropType<EditorRecipientInput>, default: null },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
    editable: { type: Boolean },
    isUpdating: { type: Boolean },
  },
  setup(props, { emit }) {
    const dateInputBoxRef = ref<Vue>(null);
    const closeOnAction = ref(false);

    const editor: Ref<TipTapEditor> = ref(null);

    const hasCleared: Ref<boolean> = ref(false);

    const fieldValue = computed(() => {
      if (props.recipientInput?.value) {
        return props.recipientInput.value;
      }

      if (props.inputField.value && !hasCleared.value) {
        return props.inputField.value;
      }
      return '';
    });

    const mergeTagValue = computed(() =>
      ProsemirrorHelper.getMergeValueFromMergeTag(
        ProsemirrorHelper.getTagIdFromMergeTag(props.inputField.value),
        props.fields,
        props.mergeValues
      )
    );

    const selectedMediumDate = computed(() =>
      EditorHelper.hasMergeTags(fieldValue.value)
        ? mergeTagValue.value
        : +fieldValue.value
        ? DateHelper.fromSecondsToMediumDate(+fieldValue.value)
        : DateHelper.fromISODateToLocaleString(fieldValue.value)
    );

    const initialFormattedDate = computed(() =>
      ProsemirrorHelper.getInputContent(selectedMediumDate.value, props.fields)
    );

    const editorConfig = computed(() => ({
      content: initialFormattedDate.value,
      editable: false,
      extensions: [
        InputDocNode,
        Focus,
        ParagraphNode,
        Text,
        MergeValueNode,
        Placeholder.configure({
          placeholder: t('input_fields_add_date'),
          showOnlyWhenEditable: false,
        }),
      ],
      onBlur: update,
      onUpdate: update,
      enableInputRules: false,
      enablePasteRules: false,
    }));

    const innerTextContent = computed(() => {
      const transaction = editor.value?.state.tr;

      return ProsemirrorHelper.getStringFromTransaction(transaction, editor.value?.state.schema);
    });

    const update = () => {
      if (!props.editable) {
        return;
      }

      const newDate = innerTextContent.value
        ? DateHelper.fromJSDateToISODate(new Date(innerTextContent.value))
        : '';
      emit('update', newDate);
    };

    const handleDateSelect = (date: Date) => {
      closeOnAction.value = true;

      const isoDate = DateHelper.fromJSDateToISODate(date);
      const localeString = DateHelper.fromISODateToLocaleString(isoDate);
      editor.value.commands.setContent(localeString);
      update();
    };

    const handleClear = () => {
      editor.value.commands.setContent('');
      if (!hasCleared.value) {
        hasCleared.value = true;
      }
      update();
    };

    const focus = () => {
      if (dateInputBoxRef.value) {
        (dateInputBoxRef.value.$el as HTMLElement).focus();
      }
    };

    watch(
      () => props.fieldFocused,
      (fieldFocused: boolean) => {
        if (fieldFocused) {
          focus();
        }
      }
    );

    watch(
      () => [props.fields, props.mergeValues],
      (newValues, oldValues) => {
        if (editor.value.isDestroyed || JSON.stringify(newValues) === JSON.stringify(oldValues)) {
          return;
        }

        editor.value.commands.setContent(
          ProsemirrorHelper.getInputContent(selectedMediumDate.value, props.fields)
        );
      }
    );

    onMounted(() => {
      editor.value = new TipTapEditor(editorConfig.value);
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    return {
      ButtonType,
      IconSize,
      editor,
      selectedMediumDate,
      closeOnAction,
      fieldValue,
      dateInputBoxRef,
      innerTextContent,
      hasCleared,
      handleClear,
      handleDateSelect,
      t,
      update,
    };
  },
});
